import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * NUC Accreditation PDF Report Generator
 * Generates professional PDF reports with branding, colors, and signatures
 */

// NUC Color Palette
const COLORS = {
  primary: '#1E3A8A',      // NUC Blue
  secondary: '#10B981',     // Success Green
  accent: '#F59E0B',        // Warning Yellow
  danger: '#EF4444',        // Danger Red
  lightGray: '#F3F4F6',
  darkGray: '#4B5563',
  black: '#111827',
  white: '#FFFFFF',
};

/**
 * Generate NUC Accreditation PDF Report
 * @param {Object} data - Report data
 * @param {Object} data.programmeDetails - Programme information
 * @param {string} data.recommendation - Accreditation recommendation
 * @param {string} data.recommendationReason - Reason for recommendation
 * @param {number} data.totalScore - Total score achieved
 * @param {number} data.maxPossibleScore - Maximum possible score
 * @param {number} data.percentage - Percentage score
 * @param {Array} data.templates - Template breakdown
 * @param {string} data.submittedAt - Submission timestamp
 * @param {boolean} data.isStage6 - Whether accreditation is in stage 6
 * @param {Array} data.panelMembers - Panel members (with chair)
 * @param {Object} data.representative - Representative who submitted
 */
export const generateAccreditationPDF = (data) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let currentY = 20;

  // Get panel chair and NUC representative for signatures
  const panelChair = data.panelMembers?.find(member => member.isChair) || data.panelMembers?.[0];
  const nucRep = data.representative;

  // Helper function to add footer with signatures on every page
  const addFooter = (pageNumber, totalPages) => {
    const footerY = pageHeight - 35;

    // Page number
    doc.setFontSize(8);
    doc.setTextColor(COLORS.darkGray);
    doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Signature lines
    doc.setFontSize(9);
    doc.setTextColor(COLORS.black);

    // Left signature (Panel Chair)
    const leftX = 20;
    doc.line(leftX, footerY, leftX + 60, footerY); // Signature line
    doc.text('Panel Chair', leftX, footerY + 5);
    if (panelChair) {
      doc.setFontSize(8);
      doc.setTextColor(COLORS.darkGray);
      doc.text(panelChair.name || 'N/A', leftX, footerY + 10);
    }

    // Right signature (NUC Representative)
    const rightX = pageWidth - 80;
    doc.line(rightX, footerY, rightX + 60, footerY); // Signature line
    doc.setFontSize(9);
    doc.setTextColor(COLORS.black);
    doc.text('NUC Representative', rightX, footerY + 5);
    if (nucRep) {
      doc.setFontSize(8);
      doc.setTextColor(COLORS.darkGray);
      doc.text(nucRep.name || nucRep.emailAddress || 'N/A', rightX, footerY + 10);
    }
  };

  // ===== PAGE 1: COVER PAGE =====
  // NUC Header Box
  doc.setFillColor(COLORS.primary);
  doc.rect(0, 0, pageWidth, 60, 'F');

  doc.setFontSize(24);
  doc.setTextColor(COLORS.white);
  doc.text('NATIONAL UNIVERSITIES COMMISSION', pageWidth / 2, 25, { align: 'center' });

  doc.setFontSize(18);
  doc.text('ACCREDITATION SUBMISSION REPORT', pageWidth / 2, 40, { align: 'center' });

  // Programme Name
  currentY = 80;
  doc.setFontSize(20);
  doc.setTextColor(COLORS.primary);
  doc.text(data.programmeDetails?.programmeName || 'N/A', pageWidth / 2, currentY, { align: 'center', maxWidth: pageWidth - 40 });

  // Institution Name
  currentY += 15;
  doc.setFontSize(14);
  doc.setTextColor(COLORS.darkGray);
  doc.text(data.programmeDetails?.institutionName || 'N/A', pageWidth / 2, currentY, { align: 'center', maxWidth: pageWidth - 40 });

  // Submission Date
  currentY += 20;
  doc.setFontSize(11);
  doc.setTextColor(COLORS.black);
  doc.text(`Submitted: ${data.submittedAt ? new Date(data.submittedAt).toLocaleDateString() : 'N/A'}`, pageWidth / 2, currentY, { align: 'center' });

  // Stage 6 Badge (if applicable)
  if (data.isStage6) {
    currentY += 15;
    doc.setFillColor(COLORS.secondary);
    doc.roundedRect(pageWidth / 2 - 45, currentY - 8, 90, 12, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(COLORS.white);
    doc.text('STAGE 6: SUBMITTED FOR REVIEW', pageWidth / 2, currentY, { align: 'center' });
  }

  // Report generation date
  currentY = pageHeight - 50;
  doc.setFontSize(9);
  doc.setTextColor(COLORS.darkGray);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, currentY, { align: 'center' });

  addFooter(1, 4); // Assuming 4 pages total (will be updated)

  // ===== PAGE 2: PROGRAMME DETAILS =====
  doc.addPage();
  currentY = 20;

  // Header
  doc.setFontSize(16);
  doc.setTextColor(COLORS.primary);
  doc.text('Programme Details', 20, currentY);
  currentY += 3;
  doc.setDrawColor(COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(20, currentY, pageWidth - 20, currentY);
  currentY += 15;

  // Details in colored boxes
  const detailsData = [
    ['Programme', data.programmeDetails?.programmeName || 'N/A'],
    ['Institution', data.programmeDetails?.institutionName || 'N/A'],
    ['Faculty', data.programmeDetails?.faculty || 'N/A'],
    ['Department', data.programmeDetails?.department || 'N/A'],
    ['Programme Type', data.programmeType || 'N/A'],
  ];

  detailsData.forEach(([label, value]) => {
    doc.setFillColor(COLORS.lightGray);
    doc.roundedRect(20, currentY - 6, pageWidth - 40, 10, 2, 2, 'F');

    doc.setFontSize(10);
    doc.setTextColor(COLORS.primary);
    doc.setFont(undefined, 'bold');
    doc.text(label + ':', 25, currentY);

    doc.setTextColor(COLORS.black);
    doc.setFont(undefined, 'normal');
    doc.text(value, 80, currentY, { maxWidth: pageWidth - 110 });

    currentY += 15;
  });

  addFooter(2, 4);

  // ===== PAGE 3: SUMMARY & RECOMMENDATION =====
  doc.addPage();
  currentY = 20;

  // Header
  doc.setFontSize(16);
  doc.setTextColor(COLORS.primary);
  doc.text('Accreditation Summary', 20, currentY);
  currentY += 3;
  doc.setDrawColor(COLORS.primary);
  doc.line(20, currentY, pageWidth - 20, currentY);
  currentY += 20;

  // Score Box
  const scoreBoxY = currentY;
  doc.setFillColor(COLORS.lightGray);
  doc.roundedRect(20, scoreBoxY, pageWidth - 40, 30, 3, 3, 'F');

  doc.setFontSize(14);
  doc.setTextColor(COLORS.primary);
  doc.text('Total Score:', 30, scoreBoxY + 12);

  doc.setFontSize(18);
  doc.setTextColor(COLORS.black);
  doc.setFont(undefined, 'bold');
  doc.text(`${data.totalScore?.toFixed(2)} / ${data.maxPossibleScore?.toFixed(2)}`, 30, scoreBoxY + 24);

  doc.setFontSize(14);
  doc.setTextColor(COLORS.primary);
  doc.setFont(undefined, 'normal');
  doc.text('Percentage:', pageWidth / 2 + 10, scoreBoxY + 12);

  doc.setFontSize(18);
  doc.setTextColor(COLORS.black);
  doc.setFont(undefined, 'bold');
  doc.text(`${data.percentage?.toFixed(2)}%`, pageWidth / 2 + 10, scoreBoxY + 24);

  currentY = scoreBoxY + 45;

  // Recommendation Badge
  let recColor = COLORS.darkGray;
  if (data.recommendation?.includes('FULL')) recColor = COLORS.secondary;
  else if (data.recommendation?.includes('INTERIM')) recColor = COLORS.accent;
  else if (data.recommendation?.includes('NOT')) recColor = COLORS.danger;

  doc.setFillColor(recColor);
  doc.roundedRect(20, currentY, pageWidth - 40, 20, 3, 3, 'F');

  doc.setFontSize(16);
  doc.setTextColor(COLORS.white);
  doc.setFont(undefined, 'bold');
  doc.text(data.recommendation || 'N/A', pageWidth / 2, currentY + 13, { align: 'center' });

  currentY += 35;

  // Recommendation Reason
  doc.setFontSize(11);
  doc.setTextColor(COLORS.black);
  doc.setFont(undefined, 'normal');
  doc.text('Assessment:', 20, currentY);
  currentY += 7;

  doc.setFontSize(10);
  doc.setTextColor(COLORS.darkGray);
  const reasonLines = doc.splitTextToSize(data.recommendationReason || 'N/A', pageWidth - 40);
  doc.text(reasonLines, 20, currentY);

  addFooter(3, 4);

  // ===== PAGE 4: TEMPLATE BREAKDOWN =====
  doc.addPage();
  currentY = 20;

  // Header
  doc.setFontSize(16);
  doc.setTextColor(COLORS.primary);
  doc.text('Template Breakdown', 20, currentY);
  currentY += 3;
  doc.setDrawColor(COLORS.primary);
  doc.line(20, currentY, pageWidth - 20, currentY);
  currentY += 10;

  // Table data
  const tableData = data.templates?.map(template => [
    template.name || 'N/A',
    template.type === 'core' ? 'Core' : 'Supplementary',
    `${template.totalScore?.toFixed(2) || 0} / ${template.maxPossibleScore?.toFixed(2) || 0}`,
    `${template.maxPossibleScore > 0 ? ((template.totalScore / template.maxPossibleScore) * 100).toFixed(1) : 0}%`,
    template.completionStatus || 'N/A',
  ]) || [];

  autoTable(doc, {
    startY: currentY,
    head: [['Template', 'Type', 'Score', 'Percentage', 'Status']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontSize: 10,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      textColor: COLORS.black,
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray,
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 35, halign: 'center' },
      2: { cellWidth: 35, halign: 'center' },
      3: { cellWidth: 30, halign: 'center' },
      4: { cellWidth: 30, halign: 'center' },
    },
    didDrawCell: (data) => {
      // Color code the Type column
      if (data.column.index === 1 && data.section === 'body') {
        if (data.cell.raw === 'Core') {
          doc.setFillColor(COLORS.secondary);
          doc.setTextColor(COLORS.white);
        }
      }
    },
  });

  addFooter(4, 4);

  // Save PDF
  const fileName = `NUC_Accreditation_Report_${data.programmeDetails?.programmeName?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
